import { useEffect, useState } from 'react';
import { PlatformApiError, type Profile, type SaveProfile } from '@sfc/sdk';
import { platform } from '../api/client';
import { PasswordSection } from './PasswordSection';
import { SkillPicker } from '../skills/SkillPicker';
import { useSkills } from '../skills/useSkills';

const AVAILABILITY = [
  ['weekends', 'Weekends'],
  ['part_time', 'Part time'],
  ['full_time', 'Full time'],
] as const;

const COLLABORATION = [
  ['cofounder_equity', 'Cofounder, for equity'],
  ['paid_freelance', 'Paid freelance'],
  ['portfolio', 'For the portfolio'],
  ['open', 'Open to anything'],
] as const;

/** What the profile endpoint accepts, and what the deck ranks by. */
const MAX_SKILLS = 12;

/**
 * What somebody says about themselves.
 *
 * This is the screen the deck's ranking depends on: without skills every
 * overlap is zero and the deck falls back to date order, which is the product
 * not doing the thing it exists to do. It is also the only place a
 * contributor appears to anybody before a match -- the holder sees these
 * answers and no name -- so the fields are the ones a stranger can judge
 * fairly, and the headline is deliberately not among what they are shown.
 */
export function ProfileScreen({ onSaved }: { onSaved: () => void }): React.JSX.Element {
  const { groups, failed } = useSkills();
  const [loading, setLoading] = useState(true);
  const [headline, setHeadline] = useState('');
  // Typed as the contract types them, so a value this screen offers and a
  // value the API accepts cannot drift apart without the build saying so.
  const [availability, setAvailability] = useState<SaveProfile['availability'] | ''>('');
  const [collaborationType, setCollaborationType] = useState<
    SaveProfile['collaborationType'] | ''
  >('');
  const [portfolioLink, setPortfolioLink] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const existing: Profile | undefined = await platform.match.profile();
        if (!live || !existing) return;
        setHeadline(existing.headline);
        setAvailability(existing.availability as SaveProfile['availability']);
        setCollaborationType(existing.collaborationType as SaveProfile['collaborationType']);
        setPortfolioLink(existing.portfolioLink ?? '');
        setSkills(existing.skills.map((skill) => skill.slug));
      } catch {
        if (live) setProblem('We could not read your profile. What you type here will still save.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const ready =
    headline.trim().length >= 2 && availability && collaborationType && skills.length > 0;

  async function save(): Promise<void> {
    if (!ready || saving) return;
    setSaving(true);
    setProblem(undefined);
    setSaved(false);

    try {
      await platform.match.saveProfile({
        headline: headline.trim(),
        availability: availability as SaveProfile['availability'],
        collaborationType: collaborationType as SaveProfile['collaborationType'],
        skills,
        // Sent only when there is one: the field is optional, and an empty
        // string is not a URL.
        ...(portfolioLink.trim() ? { portfolioLink: portfolioLink.trim() } : {}),
      });
      setSaved(true);
      // The deck ranks by these, so what is behind the other tab is now stale.
      onSaved();
    } catch (error) {
      setProblem(
        error instanceof PlatformApiError ? error.message : 'That did not save. Try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <p className="loading" data-testid="profile-loading">
        One moment…
      </p>
    );
  }

  return (
    <section className="column" aria-label="Your profile">
      <header className="column__head">
        <h2>About you</h2>
        <p className="fine">
          Idea holders see what you do, how much time you have and what you are looking for — and
          nothing that says who you are, until you both say yes.
        </p>
      </header>

      {problem ? (
        <p className="notice notice--problem" role="status" data-testid="profile-problem">
          {problem}
        </p>
      ) : null}
      {saved ? (
        <p className="notice notice--good" role="status" data-testid="profile-saved">
          Saved. Your deck is ordered by these now.
        </p>
      ) : null}

      <label className="field">
        <span>One line about you</span>
        <input
          type="text"
          maxLength={200}
          value={headline}
          placeholder="Backend engineer, nights and weekends"
          data-testid="headline"
          onChange={(event) => setHeadline(event.target.value)}
        />
        <span className="fine">
          Shown once you have matched with somebody, not before. Your name is fine here.
        </span>
      </label>

      <Choice
        label="How much time do you have?"
        name="availability"
        options={AVAILABILITY}
        value={availability}
        onChange={(next) => setAvailability(next as SaveProfile['availability'])}
      />

      <Choice
        label="What are you looking for?"
        name="collaboration"
        options={COLLABORATION}
        value={collaborationType}
        onChange={(next) => setCollaborationType(next as SaveProfile['collaborationType'])}
      />

      <label className="field">
        <span>Something of yours to look at (optional)</span>
        <input
          type="url"
          maxLength={500}
          value={portfolioLink}
          placeholder="https://"
          data-testid="portfolio"
          onChange={(event) => setPortfolioLink(event.target.value)}
        />
      </label>

      <div className="field">
        <span>What do you do?</span>
        {failed ? (
          <p className="notice notice--problem" role="status">
            We could not load the list of skills. Try again in a moment.
          </p>
        ) : (
          <SkillPicker groups={groups} chosen={skills} max={MAX_SKILLS} onChange={setSkills} />
        )}
      </div>

      <button
        className="button button--primary"
        type="button"
        disabled={!ready || saving}
        data-testid="save-profile"
        onClick={() => void save()}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>

      <PasswordSection />
    </section>
  );
}

/**
 * One answer from a short list.
 *
 * Radios rather than a `<select>`: there are three or four options, they are
 * all worth reading, and a dropdown on a phone hides them behind a tap and a
 * native wheel.
 */
function Choice({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: string;
  name: string;
  options: readonly (readonly [string, string])[];
  value: string;
  onChange: (next: string) => void;
}): React.JSX.Element {
  return (
    <fieldset className="picker__group">
      <legend className="card__label">{label}</legend>
      <div className="picker__options">
        {options.map(([option, text]) => (
          <label key={option} className={`pick${value === option ? ' pick--on' : ''}`}>
            <input
              type="radio"
              name={name}
              checked={value === option}
              onChange={() => onChange(option)}
            />
            {text}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
